/**
 * Health monitoring module
 * Handles the Health tab and header health indicator
 */

import { API } from '../core/api.js';
import { AppState, setTrackedInterval, clearTrackedInterval } from '../core/state.js';
import { startPoller } from '../core/poller.js';

// Module-level interval reference
let healthInterval = null;
let servicesHealthInterval = null;

/**
 * Load services health status
 */
export async function loadServicesHealth() {
    try {
        const data = await API.get('/api/services/health');

        // Update transcription API status
        const transcription = data.services.transcription_api;
        updateServiceStatus('transcription', transcription);

    } catch (error) {
        console.error('Error loading services health:', error);
    }
}

/**
 * Update service status display
 */
export function updateServiceStatus(serviceName, serviceData) {
    const statusElement = document.getElementById(`service-${serviceName}-status`);
    const urlElement = document.getElementById(`service-${serviceName}-url`);
    const responseElement = document.getElementById(`service-${serviceName}-response`);
    const errorElement = document.getElementById(`service-${serviceName}-error`);
    const errorRow = document.getElementById(`service-${serviceName}-error-row`);

    if (!statusElement) return;

    // Update URL
    if (urlElement) {
        urlElement.textContent = serviceData.url;
    }

    // Update response time
    if (responseElement) {
        if (serviceData.response_time_ms) {
            responseElement.textContent = `${serviceData.response_time_ms} ms`;
            responseElement.classList.remove('status-error');
        } else {
            responseElement.textContent = '-';
        }
    }

    // Update status indicator and text
    const indicator = statusElement.querySelector('.service-indicator');
    const text = statusElement.querySelector('.service-text');

    // Remove all status classes
    indicator.classList.remove('service-healthy', 'service-unhealthy', 'service-unreachable', 'service-timeout', 'service-error', 'service-unknown');

    switch(serviceData.status) {
        case 'healthy':
            indicator.classList.add('service-healthy');
            text.textContent = 'Healthy';
            text.style.color = 'var(--success)';
            if (errorRow) errorRow.style.display = 'none';
            break;
        case 'unhealthy':
            indicator.classList.add('service-unhealthy');
            text.textContent = 'Unhealthy';
            text.style.color = 'var(--warning)';
            if (errorElement && errorRow) {
                errorElement.textContent = serviceData.error || 'Service returned non-200 status';
                errorRow.style.display = '';
            }
            break;
        case 'unreachable':
            indicator.classList.add('service-unreachable');
            text.textContent = 'Unreachable';
            text.style.color = 'var(--error)';
            if (errorElement && errorRow) {
                errorElement.textContent = serviceData.error || 'Cannot connect to service';
                errorRow.style.display = '';
            }
            break;
        case 'timeout':
            indicator.classList.add('service-timeout');
            text.textContent = 'Timeout';
            text.style.color = 'var(--warning)';
            if (errorElement && errorRow) {
                errorElement.textContent = serviceData.error || 'Request timed out';
                errorRow.style.display = '';
            }
            break;
        case 'error':
            indicator.classList.add('service-error');
            text.textContent = 'Error';
            text.style.color = 'var(--error)';
            if (errorElement && errorRow) {
                errorElement.textContent = serviceData.error || 'Unknown error';
                errorRow.style.display = '';
            }
            break;
        default:
            indicator.classList.add('service-unknown');
            text.textContent = 'Unknown';
            text.style.color = 'var(--text-secondary)';
            if (errorRow) errorRow.style.display = 'none';
    }
}

/**
 * Refresh services health with button feedback
 */
export async function refreshServicesHealth() {
    const btn = document.getElementById('refresh-services-btn');
    if (btn) {
        btn.disabled = true;
        btn.textContent = '⏳ Checking...';
    }

    await loadServicesHealth();

    if (btn) {
        btn.disabled = false;
        btn.textContent = '🔄 Refresh';
    }
}

/**
 * Start services health check interval
 */
export function startServicesHealthCheck() {
    loadServicesHealth();
    // Check every 30 seconds
    servicesHealthInterval = setInterval(loadServicesHealth, 30000);
}

/**
 * Stop services health check interval
 */
export function stopServicesHealthCheck() {
    if (servicesHealthInterval) {
        clearInterval(servicesHealthInterval);
        servicesHealthInterval = null;
    }
}

/**
 * Load comprehensive health dashboard
 */
export async function loadHealthDashboard() {
    try {
        const data = await API.get('/api/services/health');

        // Update overall status
        updateOverallHealth(data.overall_status);

        // Update header indicator
        updateHeaderHealthIndicator(data.overall_status);

        // Update services
        if (data.services && data.services.transcription_api) {
            updateHealthServiceStatus('transcription', data.services.transcription_api);
        }

        // Update workers
        if (data.workers) {
            updateHealthWorkers(data.workers);
        }

        // Update system resources
        if (data.system && !data.system.error) {
            updateHealthSystemResources(data.system);
        }

        // Update application metrics
        if (data.application) {
            updateHealthApplicationMetrics(data.application);
        }

        // Update timestamp
        const checkedAt = document.getElementById('health-checked-at');
        if (checkedAt) {
            const date = new Date(data.checked_at);
            checkedAt.textContent = date.toLocaleString();
        }

    } catch (error) {
        console.error('Error loading health dashboard:', error);
    }
}

/**
 * Update overall health status card
 */
export function updateOverallHealth(status) {
    const card = document.getElementById('health-overall-card');
    const icon = document.getElementById('health-overall-icon');
    const statusText = document.getElementById('health-overall-status');
    const message = document.getElementById('health-overall-message');

    if (!card) return;

    // Remove all status classes
    card.className = 'health-overall-card';

    if (status === 'healthy') {
        card.classList.add('health-overall-healthy');
        icon.textContent = '✅';
        statusText.textContent = 'System Healthy';
        message.textContent = 'All systems operational';
    } else if (status === 'degraded') {
        card.classList.add('health-overall-degraded');
        icon.textContent = '⚠️';
        statusText.textContent = 'System Degraded';
        message.textContent = 'Some components experiencing issues';
    } else {
        card.classList.add('health-overall-unhealthy');
        icon.textContent = '❌';
        statusText.textContent = 'System Unhealthy';
        message.textContent = 'Critical issues detected';
    }
}

/**
 * Update header health indicator
 */
export function updateHeaderHealthIndicator(status) {
    const dot = document.getElementById('health-dot');
    const text = document.getElementById('health-text');

    if (!dot || !text) return;

    // Remove all status classes
    dot.className = 'health-dot';

    if (status === 'healthy') {
        dot.classList.add('health-healthy');
        text.textContent = 'Healthy';
    } else if (status === 'degraded') {
        dot.classList.add('health-degraded');
        text.textContent = 'Degraded';
    } else {
        dot.classList.add('health-unhealthy');
        text.textContent = 'Unhealthy';
    }
}

/**
 * Update health service status
 */
export function updateHealthServiceStatus(serviceName, serviceData) {
    const statusElement = document.getElementById(`health-service-${serviceName}-status`);
    const urlElement = document.getElementById(`health-service-${serviceName}-url`);
    const responseElement = document.getElementById(`health-service-${serviceName}-response`);
    const errorElement = document.getElementById(`health-service-${serviceName}-error`);
    const errorRow = document.getElementById(`health-service-${serviceName}-error-row`);

    if (!statusElement) return;

    // Update URL
    if (urlElement) {
        urlElement.textContent = serviceData.url;
    }

    // Update response time
    if (responseElement) {
        if (serviceData.response_time_ms) {
            responseElement.textContent = `${serviceData.response_time_ms} ms`;
        } else {
            responseElement.textContent = '-';
        }
    }

    // Update status indicator
    const indicator = statusElement.querySelector('.service-indicator');
    const text = statusElement.querySelector('.service-text');

    indicator.classList.remove('service-healthy', 'service-unhealthy', 'service-unreachable', 'service-timeout', 'service-error', 'service-unknown');

    switch(serviceData.status) {
        case 'healthy':
            indicator.classList.add('service-healthy');
            text.textContent = 'Healthy';
            text.style.color = 'var(--success)';
            if (errorRow) errorRow.style.display = 'none';
            break;
        case 'unreachable':
            indicator.classList.add('service-unreachable');
            text.textContent = 'Unreachable';
            text.style.color = 'var(--error)';
            if (errorElement && errorRow) {
                errorElement.textContent = serviceData.error || 'Cannot connect';
                errorRow.style.display = '';
            }
            break;
        default:
            indicator.classList.add('service-error');
            text.textContent = serviceData.status;
            text.style.color = 'var(--error)';
            if (errorElement && errorRow && serviceData.error) {
                errorElement.textContent = serviceData.error;
                errorRow.style.display = '';
            }
    }
}

/**
 * Update health workers status
 */
export function updateHealthWorkers(workers) {
    // Download worker
    const downloadStatus = document.getElementById('health-worker-download-status');
    const downloadQueue = document.getElementById('health-worker-download-queue');
    const downloadCompleted = document.getElementById('health-worker-download-completed');
    const downloadFailed = document.getElementById('health-worker-download-failed');
    const downloadLastRun = document.getElementById('health-worker-download-lastrun');

    if (downloadStatus) downloadStatus.textContent = workers.downloader.status || '-';
    if (downloadQueue) downloadQueue.textContent = workers.downloader.queue_size || '0';
    if (downloadCompleted) downloadCompleted.textContent = workers.downloader.completed || '0';
    if (downloadFailed) downloadFailed.textContent = workers.downloader.failed || '0';
    if (downloadLastRun) {
        if (workers.downloader.last_run) {
            const date = new Date(workers.downloader.last_run);
            downloadLastRun.textContent = date.toLocaleString();
        } else {
            downloadLastRun.textContent = 'Never';
        }
    }

    // Processing worker
    const processStatus = document.getElementById('health-worker-process-status');
    const processQueue = document.getElementById('health-worker-process-queue');
    const processCompleted = document.getElementById('health-worker-process-completed');
    const processFailed = document.getElementById('health-worker-process-failed');
    const processLastRun = document.getElementById('health-worker-process-lastrun');

    if (processStatus) processStatus.textContent = workers.processor.status || '-';
    if (processQueue) processQueue.textContent = workers.processor.queue_size || '0';
    if (processCompleted) processCompleted.textContent = workers.processor.completed || '0';
    if (processFailed) processFailed.textContent = workers.processor.failed || '0';
    if (processLastRun) {
        if (workers.processor.last_run) {
            const date = new Date(workers.processor.last_run);
            processLastRun.textContent = date.toLocaleString();
        } else {
            processLastRun.textContent = 'Never';
        }
    }
}

/**
 * Update system resources display
 */
export function updateHealthSystemResources(system) {
    // CPU
    const cpuPercent = document.getElementById('health-cpu-percent');
    const cpuBar = document.getElementById('health-cpu-bar');
    const cpuCount = document.getElementById('health-cpu-count');

    if (cpuPercent) cpuPercent.textContent = `${system.cpu.percent}%`;
    if (cpuBar) {
        cpuBar.style.width = `${system.cpu.percent}%`;
        cpuBar.className = 'progress-bar-fill';
        if (system.cpu.percent > 80) cpuBar.classList.add('progress-bar-error');
        else if (system.cpu.percent > 60) cpuBar.classList.add('progress-bar-warning');
        else cpuBar.classList.add('progress-bar-success');
    }
    if (cpuCount) cpuCount.textContent = system.cpu.count;

    // Memory
    const memoryPercent = document.getElementById('health-memory-percent');
    const memoryBar = document.getElementById('health-memory-bar');
    const memoryUsed = document.getElementById('health-memory-used');
    const memoryTotal = document.getElementById('health-memory-total');

    if (memoryPercent) memoryPercent.textContent = `${system.memory.percent}%`;
    if (memoryBar) {
        memoryBar.style.width = `${system.memory.percent}%`;
        memoryBar.className = 'progress-bar-fill';
        if (system.memory.percent > 80) memoryBar.classList.add('progress-bar-error');
        else if (system.memory.percent > 60) memoryBar.classList.add('progress-bar-warning');
        else memoryBar.classList.add('progress-bar-success');
    }
    if (memoryUsed) memoryUsed.textContent = `${system.memory.used_gb} GB`;
    if (memoryTotal) memoryTotal.textContent = `${system.memory.total_gb} GB`;

    // Disk
    const diskPercent = document.getElementById('health-disk-percent');
    const diskBar = document.getElementById('health-disk-bar');
    const diskUsed = document.getElementById('health-disk-used');
    const diskFree = document.getElementById('health-disk-free');

    if (diskPercent) diskPercent.textContent = `${system.disk.percent}%`;
    if (diskBar) {
        diskBar.style.width = `${system.disk.percent}%`;
        diskBar.className = 'progress-bar-fill';
        if (system.disk.percent > 80) diskBar.classList.add('progress-bar-error');
        else if (system.disk.percent > 60) diskBar.classList.add('progress-bar-warning');
        else diskBar.classList.add('progress-bar-success');
    }
    if (diskUsed) diskUsed.textContent = `${system.disk.used_gb} GB`;
    if (diskFree) diskFree.textContent = `${system.disk.free_gb} GB`;
}

/**
 * Update application metrics display
 */
export function updateHealthApplicationMetrics(app) {
    const version = document.getElementById('health-app-version');
    const environment = document.getElementById('health-app-environment');
    const uptime = document.getElementById('health-app-uptime');
    const started = document.getElementById('health-app-started');

    if (version) version.textContent = app.version;
    if (environment) {
        environment.textContent = app.environment;
        environment.className = 'status-value';
        if (app.environment === 'production') {
            environment.classList.add('status-error');
        } else {
            environment.classList.add('status-warning');
        }
    }
    if (uptime) uptime.textContent = app.uptime;
    if (started) {
        const date = new Date(app.started_at);
        started.textContent = date.toLocaleString();
    }

    // Database stats
    const podcasts = document.getElementById('health-db-podcasts');
    const episodes = document.getElementById('health-db-episodes');
    const downloaded = document.getElementById('health-db-downloaded');
    const processed = document.getElementById('health-db-processed');
    const downloadRate = document.getElementById('health-db-download-rate');
    const processRate = document.getElementById('health-db-process-rate');

    if (podcasts) podcasts.textContent = app.database.total_podcasts;
    if (episodes) episodes.textContent = app.database.total_episodes;
    if (downloaded) downloaded.textContent = app.database.downloaded_episodes;
    if (processed) processed.textContent = app.database.processed_episodes;
    if (downloadRate) downloadRate.textContent = `${app.database.download_rate}%`;
    if (processRate) processRate.textContent = `${app.database.processing_rate}%`;
}

/**
 * Refresh health dashboard with button feedback
 */
export async function refreshHealthDashboard() {
    const btn = document.getElementById('refresh-health-btn');
    if (btn) {
        btn.disabled = true;
        btn.textContent = '⏳ Refreshing...';
    }

    await loadHealthDashboard();

    if (btn) {
        btn.disabled = false;
        btn.textContent = '🔄 Refresh';
    }
}

/**
 * Start health monitoring
 */
export function startHealthMonitoring() {
    loadHealthDashboard();
    // Auto-refresh every 30 seconds
    healthInterval = setInterval(loadHealthDashboard, 30000);
}

/**
 * Stop health monitoring
 */
export function stopHealthMonitoring() {
    if (healthInterval) {
        clearInterval(healthInterval);
        healthInterval = null;
    }
}

/**
 * Initialize header health indicator
 */
export function initHeaderHealthIndicator() {
    // Load immediately; poll every 60s with backoff. Services health hits the
    // external whisper API (~500 ms), no need to bang on it every half minute.
    loadHealthDashboard();
    startPoller('header-health', async () => {
        const data = await API.get('/api/services/health');
        updateHeaderHealthIndicator(data.overall_status);
    }, { baseMs: 60000, maxMs: 300000 });
}
